import { z } from "../zod.js";
import { Uuid, WorkCenterNameI18n } from "../identity/admin-schemas.js";

// THE NAMES READ (spec 16 §3.13, design-system §3.9; P3-UX UP6, D-441, D-442).
//
// A screen that holds an id from another module's record (a hold's "placed
// by", an inspection's subject, an audit row's actor) used to print the uuid,
// because the record it read carries the id and not the name. This read turns
// a batch of `{kind, id}` pairs into names, each answered by the module that
// owns the row and only for a kind the caller may read.
//
// WHAT A MISSING ITEM MEANS. The answer lists only the references it named.
// An id that does not exist, belongs to another tenant, sits outside the
// caller's scope, or is of a kind the caller may not read is simply absent,
// and the four are indistinguishable on purpose (spec 16 §3.13's threat
// model): an absent item is the short-id chip on screen, never a 403 or a 404
// that would confirm the id exists.

/** The kinds the read names, each owned by one module (spec 16 §3.13). */
export const REF_KINDS = [
  "user",
  "agent",
  "facility",
  "work_center",
  "product",
  "stock_unit",
  "lot",
  "location",
  "customer",
] as const;

export const RefKind = z.enum(REF_KINDS).openapi("RefKind");
export type RefKindName = (typeof REF_KINDS)[number];

/** The most references one read may name. A larger batch is refused with 400,
 * never truncated, so a caller is never answered for half of what it asked. */
export const REFS_BATCH_MAX = 100;

const UUID_SOURCE = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

/** One reference on the wire: `<kind>:<uuid>`, e.g. `user:0197…`. */
export const RefKey = z
  .string()
  .regex(new RegExp(`^(${REF_KINDS.join("|")}):${UUID_SOURCE}$`), "a <kind>:<uuid> reference")
  .openapi("RefKey");

/** Split a validated `RefKey` into its kind and id. */
export function parseRefKey(key: string): { kind: RefKindName; id: string } {
  const at = key.indexOf(":");
  return { kind: key.slice(0, at) as RefKindName, id: key.slice(at + 1).toLowerCase() };
}

/** Build the wire form of a reference. */
export function refKey(kind: RefKindName, id: string): string {
  return `${kind}:${id}`;
}

/** Express hands a single `ref` as a string and a repeated one as an array. */
const oneOrMany = (value: unknown): unknown =>
  value === undefined ? undefined : Array.isArray(value) ? value : [value];

export const RefsQuery = z.object({
  ref: z.preprocess(oneOrMany, z.array(RefKey).min(1).max(REFS_BATCH_MAX)),
});

export const RefLabelItem = z
  .object({
    kind: RefKind,
    id: Uuid,
    /** The name in the owning module's own words: a member's display name, a
     * product's name, a stock unit's serial, a lot's code, a location's or a
     * facility's name, a customer's display name, a work centre's name in the
     * first locale it carries (en, then ru, zh, fr). */
    label: z.string().min(1),
    /** A short code beside the name where the row has one: a product's SKU, a
     * facility's, location's or work centre's code, a customer's number. */
    code: z.string().optional(),
    /** A work centre's name in every locale it carries, so the client picks
     * the reader's language. Absent for every other kind. */
    label_i18n: WorkCenterNameI18n.optional(),
  })
  .openapi("RefLabelItem");

export const RefsResponse = z
  .object({
    /** One item per reference the caller may see, in no particular order.
     * A reference the read could not name is absent. */
    items: z.array(RefLabelItem),
  })
  .openapi("RefsResponse");

export type RefLabelItemJson = z.infer<typeof RefLabelItem>;
export type RefsResponseJson = z.infer<typeof RefsResponse>;

/**
 * The read codes each kind is answered under (spec 16 §3.13). A kind is named
 * only for a caller who holds one of its codes, and only within the scope of
 * the grants that carry one, exactly as the owning module's own read would
 * answer that caller. A person (user or agent) is named for the member list's
 * code or for `identity.view_member_names`, the code that names co-members on
 * the records a role reads without opening the member list (D-442).
 */
export const REF_KIND_READ_CODES: Readonly<Record<RefKindName, readonly string[]>> = {
  user: ["identity.view_members", "identity.view_member_names"],
  agent: ["identity.view_members", "identity.view_member_names"],
  facility: ["identity.view_org"],
  work_center: ["identity.view_org"],
  product: ["catalogue.read"],
  stock_unit: ["inventory.read"],
  lot: ["inventory.read"],
  location: ["inventory.read"],
  customer: ["sales.read"],
};
